/**
 * “文字优先”的节点自动定尺寸 + 间距计算层。
 *
 * 设计目标（对应节点/连线排版问题的修复）：
 * 1. 节点尺寸必须由真实文字内容反推，而不是预置一个固定值再让文字自适应；
 * 2. 不允许省略必要文字的节点（如过程节点）按“允许换行、不允许截断”的策略
 *    计算尺寸；允许省略的节点（如结果节点）则固定“最大行数”的盒子尺寸，
 *    文字超出后按最大行数截断；
 * 3. 节点间的最短连线长度是一个可调常量（DEFAULT_MIN_EDGE_LENGTH），
 *    所有分层/分行间距计算都应以它为下限。
 */

import { wrapLabelText, measureTextWidth } from './textMetrics';
import type { TextMetricsOptions } from './textMetrics';

/** 默认最短连线长度（世界单位），可通过参数覆盖。 */
export const DEFAULT_MIN_EDGE_LENGTH = 90;

export interface AutoWrapBoxOptions extends TextMetricsOptions {
  lineHeight: number;
  paddingX: number;
  paddingY: number;
  minWidth?: number;
  minHeight?: number;
  /** 换行宽度预算：文字优先在此宽度内换行，而不是被截断 */
  maxWidth: number;
  /** 不传则不限制行数，文字永不截断（用于“文字不可省略”的节点） */
  maxLines?: number;
}

export interface AutoWrapBoxResult {
  /** 换行后的文本，行与行之间以 \n 连接，渲染时按 pre-wrap 展示即可 */
  label: string;
  width: number;
  height: number;
  lines: string[];
  truncated: boolean;
}

/**
 * 根据真实文字内容计算“恰好包住文字”的盒子尺寸（宽高均由内容驱动）。
 * 用于文字不可省略的节点类型（如过程节点）：宽度不够时优先换行增高，
 * 而不是压缩/截断文字。
 */
export function autoWrapTextBox(
  text: string,
  options: AutoWrapBoxOptions
): AutoWrapBoxResult {
  const wrapWidth = Math.max(1, options.maxWidth - options.paddingX);
  const { lines, truncated } = wrapLabelText(text, {
    fontSize: options.fontSize,
    fontFamily: options.fontFamily,
    fontWeight: options.fontWeight,
    maxWidth: wrapWidth,
    maxLines: options.maxLines,
  });
  const contentWidth = Math.max(
    ...lines.map(line => measureTextWidth(line, options)),
    0
  );
  const width = Math.max(
    options.minWidth ?? 0,
    Math.min(options.maxWidth, Math.ceil(contentWidth + options.paddingX))
  );
  const height = Math.max(
    options.minHeight ?? 0,
    Math.ceil(lines.length * options.lineHeight + options.paddingY)
  );
  return { label: lines.join('\n'), width, height, lines, truncated };
}

export interface FixedLinesBoxConfig extends TextMetricsOptions {
  lineHeight: number;
  paddingX: number;
  paddingY: number;
  /** 固定宽度（所有该类型节点共用同一宽度，不随内容变化） */
  width: number;
  /** 固定最大行数，超出则截断并追加省略号 */
  maxLines: number;
}

/** 根据“固定宽度 + 最大行数”预算，计算固定高度（与具体文字内容无关）。 */
export function computeFixedLinesBoxHeight(
  config: Pick<FixedLinesBoxConfig, 'lineHeight' | 'paddingY' | 'maxLines'>
): number {
  return Math.ceil(config.maxLines * config.lineHeight + config.paddingY);
}

export interface FitTextToFixedBoxResult {
  label: string;
  truncated: boolean;
}

/**
 * 将任意长度文字装入“固定宽高”的盒子：在固定宽度下换行，超出最大行数的
 * 部分截断并追加省略号。用于允许省略的节点类型（如结果节点）：节点尺寸
 * 恒定，文字按预算裁剪，永远不会撑破节点边框。
 */
export function fitTextToFixedBox(
  text: string,
  config: FixedLinesBoxConfig
): FitTextToFixedBoxResult {
  const wrapWidth = Math.max(1, config.width - config.paddingX);
  const { lines, truncated } = wrapLabelText(text, {
    fontSize: config.fontSize,
    fontFamily: config.fontFamily,
    fontWeight: config.fontWeight,
    maxWidth: wrapWidth,
    maxLines: config.maxLines,
  });
  return { label: lines.join('\n'), truncated };
}

/**
 * 按“层级（如树的深度）”计算每一层的位置偏移量，保证相邻两层之间的间距
 * 不小于：上一层最大半宽 + minEdgeLength + 下一层最大半宽。
 *
 * @param maxHalfSizeByLayer 每一层节点的最大半宽（或半高），key 为层级标识
 * @param minEdgeLength 相邻层节点边界之间的最短距离
 */
export function computeLayerOffsets(
  maxHalfSizeByLayer: Map<number, number>,
  minEdgeLength: number = DEFAULT_MIN_EDGE_LENGTH
): Map<number, number> {
  const layers = [...maxHalfSizeByLayer.keys()].sort((a, b) => a - b);
  const offsets = new Map<number, number>();
  let cursor = 0;
  layers.forEach((layer, index) => {
    if (index === 0) {
      cursor = 0;
    } else {
      const prevLayer = layers[index - 1];
      cursor +=
        (maxHalfSizeByLayer.get(prevLayer) ?? 0) +
        minEdgeLength +
        (maxHalfSizeByLayer.get(layer) ?? 0);
    }
    offsets.set(layer, cursor);
  });
  return offsets;
}

/**
 * 按顺序堆叠一组“半尺寸”不同的项，返回每一项的中心位置偏移量，
 * 保证相邻两项边界之间的间距不小于 minGap。
 */
export function computeSequentialOffsets(
  halfSizes: number[],
  minGap: number = DEFAULT_MIN_EDGE_LENGTH
): number[] {
  const offsets: number[] = [];
  let previousOffset = 0;
  let previousHalf = 0;
  halfSizes.forEach((halfSize, index) => {
    const offset =
      index === 0
        ? halfSize
        : previousOffset + previousHalf + minGap + halfSize;
    offsets.push(offset);
    previousOffset = offset;
    previousHalf = halfSize;
  });
  return offsets;
}
